<template>
  <el-container class="chat-page">
    <resizable-aside :default-width="260" :min-width="200" :max-width="500" storage-key="chat-aside-width">
      <div class="header" :class="configStore.electronMode ? 'header-menu-wrap' : ''">
        <div class="search-wrap" ref="searchWrap">
          <el-input class="search-text" size="small" placeholder="搜索联系人、群聊、聊天记录" v-model="searchText"
            @focus="onSearchFocus" @keyup.enter.native="onSearchEnter">
            <i class="el-icon-search el-input__icon" slot="prefix"> </i>
          </el-input>
          <div v-show="showSearchPopup" class="search-popup">
            <div v-if="searchLoading" class="search-tip">
              <i class="el-icon-loading"></i>
              <span>搜索中...</span>
            </div>
            <div v-else-if="!keyword" class="search-tip">输入关键词后按回车开始搜索</div>
            <template v-else>
              <div v-if="hasSearched && contactResults.length === 0 && groupResults.length === 0 && messageResults.length === 0"
                class="search-tip">
                未找到相关内容
              </div>
              <div v-if="contactResults.length > 0" class="search-section">
                <div class="section-title">联系人</div>
                <div v-for="item in contactResults" :key="'c-' + item.id" class="result-row chat-item-like"
                  @click="openUser(item.id)">
                  <div class="chat-left">
                    <head-image :name="item.showName" :size="45" :url="item.headImage"></head-image>
                  </div>
                  <div class="chat-right">
                    <div class="chat-name">
                      <div class="chat-name-text" v-html="highlightText(item.showName)"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="groupResults.length > 0" class="search-section">
                <div class="section-title">群聊</div>
                <div v-for="item in groupResults" :key="'g-' + item.id" class="result-row chat-item-like"
                  @click="openGroup(item.id)">
                  <div class="chat-left">
                    <head-image :name="item.showName" :size="45"></head-image>
                  </div>
                  <div class="chat-right">
                    <div class="chat-name">
                      <div class="chat-name-text" v-html="highlightText(item.showName)"></div>
                    </div>
                    <div v-if="item.includeText" class="chat-content">
                      <div class="chat-content-text" v-html="highlightText(item.includeText)"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="messageResults.length > 0" class="search-section">
                <div class="section-title">聊天记录</div>
                <div v-for="(item, idx) in messageResults" :key="'m-' + idx" class="result-row chat-item-like"
                  @click="openMessage(item)">
                  <div class="chat-left">
                    <head-image :name="item.groupName" :size="45"></head-image>
                  </div>
                  <div class="chat-right">
                    <div class="chat-name">
                      <div class="chat-name-text" v-html="highlightText(item.groupName)"></div>
                    </div>
                    <div class="chat-content">
                      <div class="chat-content-text" v-html="highlightText(item.preview)"></div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
      <div class="chat-loading" v-if="loading" v-loading="true" element-loading-text="消息接收中..."
        element-loading-spinner="el-icon-loading" element-loading-background="#F9F9F9" element-loading-size="24">
      </div>
      <virtual-scroller ref="chatScroller" class="scroll-box" :items="showChats">
        <template v-slot="{ item }">
          <div :data-chat-id="chatItemId(item)">
            <chat-item :chat="item" @click.native="onActiveItem(item)" @delete="onDelItem(item)" @info="onShowInfo(item)"
              @dnd="onDnd(item)" @top="onTop(item)" :active="item === chatStore.activeChat"></chat-item>
          </div>
        </template>
      </virtual-scroller>
    </resizable-aside>
    <el-container class="chat-box">
      <chat-box v-if="activeChat && activeChat.type != 'SYSTEM'" :chat="activeChat"></chat-box>
      <chat-system-box v-if="activeChat && activeChat.type == 'SYSTEM'" :chat="activeChat"></chat-system-box>
    </el-container>
  </el-container>
</template>

<script>
import ChatItem from "../components/chat/ChatItem.vue";
import ChatBox from "../components/chat/ChatBox.vue";
import ChatSystemBox from "../components/chat/ChatSystemBox.vue";
import ResizableAside from "../components/common/ResizableAside.vue";
import VirtualScroller from "../components/common/VirtualScroller.vue";
import HeadImage from "../components/common/HeadImage.vue";
export default {
  name: "chat",
  components: {
    ChatItem,
    ChatBox,
    ChatSystemBox,
    ResizableAside,
    VirtualScroller,
    HeadImage
  },
  data() {
    return {
      searchText: "",
      searchKeyword: "",
      searchLoading: false,
      hasSearched: false,
      showSearchPopup: false,
      lastUnreadLocateKey: "",
      messageContent: "",
      group: {},
      groupMembers: []
    }
  },
  methods: {
    waitForPaint() {
      return new Promise(resolve => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });
    },
    clearSearchState() {
      this.searchText = "";
      this.searchKeyword = "";
      this.searchLoading = false;
      this.hasSearched = false;
    },
    onSearchFocus() {
      this.showSearchPopup = true;
      this.preloadGroupMembers();
    },
    async onSearchEnter() {
      this.showSearchPopup = true;
      this.preloadGroupMembers();
      const next = String(this.searchText || "").trim();
      if (!next) {
        this.searchKeyword = "";
        this.searchLoading = false;
        this.hasSearched = false;
        return;
      }
      this.searchLoading = true;
      this.hasSearched = false;
      // 先把“搜索中”渲染出来，避免用户感知为卡顿
      await this.$nextTick();
      await this.waitForPaint();
      this.searchKeyword = next;
      await this.$nextTick();
      this.searchLoading = false;
      this.hasSearched = true;
      this.searchText = "";
    },
    onGlobalClick(e) {
      const wrap = this.$refs.searchWrap;
      if (wrap && !wrap.contains(e.target)) {
        this.showSearchPopup = false;
        this.clearSearchState();
      }
    },
    preloadGroupMembers() {
      this.groupStore.groups.filter(g => !g.quit).forEach(g => {
        if (!Array.isArray(g.members) || g.members.length === 0) {
          this.groupStore.refreshMember(g.id);
        }
      });
    },
    getSearchChats() {
      if (typeof this.chatStore.findChats === "function") {
        return this.chatStore.findChats() || [];
      }
      return this.chatStore.chats || [];
    },
    buildSnippet(text, keyword) {
      if (!text) return "";
      const source = String(text);
      const lower = source.toLowerCase();
      const key = String(keyword || "").toLowerCase();
      const idx = lower.indexOf(key);
      if (idx < 0) return source.length > 80 ? source.slice(0, 80) + "..." : source;
      const start = Math.max(0, idx - 12);
      const end = Math.min(source.length, idx + key.length + 24);
      let snippet = source.slice(start, end);
      if (start > 0) snippet = "..." + snippet;
      if (end < source.length) snippet = snippet + "...";
      return snippet;
    },
    getSearchableText(msg) {
      if (!msg || msg.type === undefined || msg.type === null) return "";
      if (msg.type == this.$enums.MESSAGE_TYPE.TEXT) return String(msg.content || "");
      if (msg.type == this.$enums.MESSAGE_TYPE.FILE) {
        try {
          const data = (msg.content && typeof msg.content === "object") ? msg.content : JSON.parse(msg.content || "{}");
          return (data && data.name) ? String(data.name) : "";
        } catch (e) {
          return "";
        }
      }
      if (msg.type == this.$enums.MESSAGE_TYPE.RICH_TEXT) {
        try {
          const data = (msg.content && typeof msg.content === "object") ? msg.content : JSON.parse(msg.content || "{}");
          const blocks = Array.isArray(data.blocks) ? data.blocks : [];
          return blocks.filter(b => b && b.type === "text").map(b => b.content || "").join(" ");
        } catch (e) { return ""; }
      }
      return "";
    },
    messageMatchesKeyword(msg, keyword) {
      if (!msg || msg.type === undefined || msg.type === null || !keyword) return false;
      const key = String(keyword).toLowerCase();
      if (this.$enums.MESSAGE_TYPE.TEXT == msg.type) {
        return String(msg.content || "").toLowerCase().includes(key);
      } else if (this.$enums.MESSAGE_TYPE.FILE == msg.type) {
        try {
          const data = (msg.content && typeof msg.content === "object") ? msg.content : JSON.parse(msg.content || "{}");
          return String((data && data.name) ? data.name : "").toLowerCase().includes(key);
        } catch (e) {
          return false;
        }
      } else if (this.$enums.MESSAGE_TYPE.RICH_TEXT == msg.type) {
        try {
          const data = (msg.content && typeof msg.content === "object") ? msg.content : JSON.parse(msg.content || "{}");
          const blocks = Array.isArray(data.blocks) ? data.blocks : [];
          return blocks.some(b =>
            b && b.type === "text" && String(b.content || "").toLowerCase().includes(key)
          );
        } catch (e) {
          return false;
        }
      }
      return false;
    },
    groupMatchInfo(group, keyword) {
      if (!group || !keyword) {
        return {
          matched: false,
          includeText: ""
        };
      }
      const key = String(keyword).toLowerCase();
      const groupName = String(group.showGroupName || "").toLowerCase();
      if (groupName.includes(key)) {
        return {
          matched: true,
          includeText: ""
        };
      }
      const members = Array.isArray(group.members) ? group.members : [];
      for (const m of members) {
        const showName = String(m && m.showNickName || "").toLowerCase();
        const nickName = String(m && m.nickName || "").toLowerCase();
        if (showName.includes(key) || nickName.includes(key)) {
          return {
            matched: true,
            includeText: `包含：${String(m && m.showNickName || m && m.nickName || "")}`
          };
        }
        const friend = m && m.userId ? this.friendStore.findFriend(m.userId) : null;
        const remarkName = String(friend && friend.remarkNickName || "").toLowerCase();
        const friendShowName = String(friend && friend.showNickName || "").toLowerCase();
        if (remarkName.includes(key) || friendShowName.includes(key)) {
          const displayName = String(
            friend && (friend.remarkNickName || friend.showNickName || friend.nickName) ||
            m && (m.showNickName || m.nickName) ||
            ""
          );
          return {
            matched: true,
            includeText: `包含：${displayName}`
          };
        }
      }
      return {
        matched: false,
        includeText: ""
      };
    },
    escapeHtml(text) {
      return String(text || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    },
    highlightText(text) {
      const safe = this.escapeHtml(text);
      if (!this.keyword) return safe;
      const kw = this.escapeHtml(this.keyword).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const reg = new RegExp(`(${kw})`, "ig");
      return safe.replace(reg, '<span class="kw">$1</span>');
    },
    openUser(id) {
      this.showSearchPopup = false;
      this.clearSearchState();
      this.$router.push(`/home/friend?id=${id}`);
    },
    openGroup(id) {
      this.showSearchPopup = false;
      this.clearSearchState();
      this.$router.push(`/home/group?id=${id}`);
    },
    openMessage(item) {
      this.showSearchPopup = false;
      this.clearSearchState();
      this.chatStore.setActiveChat(item.chat);
      if (item.messageId) {
        this.$nextTick(() => this.$eventBus.$emit("locateMessageById", item.messageId));
      }
    },
    chatItemId(chat) {
      return `chat-item-${chat.type}-${chat.targetId}`;
    },
    chatKey(chat) {
      return `${chat.type}-${chat.targetId}`;
    },
    locateNextUnreadChat() {
      // 先恢复完整会话列表（避免搜索过滤影响定位）
      this.searchText = "";
      this.$nextTick(() => {
        const chats = this.chatStore.chats || [];
        const len = chats.length;
        if (!len) {
          this.$message.info("没有未读会话");
          return;
        }
        // 只定位“会话列表显示未读数字”的那些：ChatItem.vue 里是 v-show="!chat.isDnd && chat.unreadCount > 0"
        let startIdx = 0;
        if (this.lastUnreadLocateKey) {
          const lastIdx = chats.findIndex(c => this.chatKey(c) === this.lastUnreadLocateKey);
          if (lastIdx >= 0) startIdx = (lastIdx + 1) % len;
        }
        let idx = -1;
        for (let step = 0; step < len; step++) {
          const i = (startIdx + step) % len;
          const chat = chats[i];
          if (chat && !chat.delete && !chat.isDnd && chat.unreadCount > 0) {
            idx = i;
            break;
          }
        }
        if (idx < 0) {
          this.$message.info("没有未读会话");
          return;
        }

        const targetChat = chats[idx];
        this.lastUnreadLocateKey = this.chatKey(targetChat);

        const scroller = this.$refs.chatScroller;
        if (scroller) {
          const size = scroller.size || 30;
          scroller.page = Math.max(1, Math.ceil((idx + 1) / size));
        }
        this.$nextTick(() => {
          const selector = `[data-chat-id="${this.chatItemId(targetChat)}"]`;
          const target = this.$el.querySelector(selector);
          target && target.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      });
    },
    locateFirstUnreadChat() {
      // 恢复完整列表后查找第一个未读会话
      this.searchText = "";
      this.$nextTick(() => {
        const idx = this.chatStore.chats.findIndex(chat => !chat.delete && chat.unreadCount > 0);
        if (idx < 0) {
          this.$message.info("没有未读会话");
          return;
        }
        const scroller = this.$refs.chatScroller;
        if (scroller) {
          const size = scroller.size || 30;
          scroller.page = Math.max(1, Math.ceil((idx + 1) / size));
        }
        this.$nextTick(() => {
          const chat = this.chatStore.chats[idx];
          const selector = `[data-chat-id="${this.chatItemId(chat)}"]`;
          const target = this.$el.querySelector(selector);
          target && target.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      });
    },
    onActiveItem(chat) {
      this.chatStore.setActiveChat(chat);
    },
    onDelItem(chat) {
      this.$confirm(`删除后记录将清空,确认删除与' ${chat.showName}'的聊天 ?`, '删除会话', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        if (chat.type == 'PRIVATE' || chat.type == 'GROUP') {
            const data = {
              chatId: chat.targetId
            }
            await this.$http({
              url: `/message/${chat.type.toLowerCase()}/deleteChat`,
              method: 'delete',
              data: data
            });
        }
        this.chatStore.removeChat(chat);
      });
    },
    onShowInfo(chat) {
      if (chat.type == 'PRIVATE') {
        this.$router.push("/home/friend?id=" + chat.targetId);
      } else if (chat.type == 'GROUP') {
        if (!this.groupStore.isGroup(chat.targetId)) {
          this.$message.error("您已不在群聊中，无法查看群资料")
          return;
        }
        this.$router.push("/home/group?id=" + chat.targetId);
      }
    },
    onDnd(chat) {
      if (chat.type == 'PRIVATE') {
        this.setFriendDnd(chat, chat.targetId, !chat.isDnd)
      } else {
        this.setGroupDnd(chat, chat.targetId, !chat.isDnd)
      }
    },
    setFriendDnd(chat, friendId, isDnd) {
      let formData = {
        friendId: friendId,
        isDnd: isDnd
      }
      this.$http({
        url: '/friend/dnd',
        method: 'put',
        data: formData
      }).then(() => {
        this.friendStore.setDnd(friendId, isDnd)
        this.chatStore.setDnd(chat, isDnd)
      })
    },
    setGroupDnd(chat, groupId, isDnd) {
      let formData = {
        groupId: groupId,
        isDnd: isDnd
      }
      this.$http({
        url: '/group/dnd',
        method: 'put',
        data: formData
      }).then(() => {
        this.groupStore.setDnd(groupId, isDnd)
        this.chatStore.setDnd(chat, isDnd)
      })
    },
    onTop(chat) {
      if (chat.type == 'PRIVATE') {
        this.setFriendTop(chat, chat.targetId, !chat.isTop)
      } else {
        this.setGroupTop(chat, chat.targetId, !chat.isTop)
      }
    },
    setFriendTop(chat, friendId, isTop) {
      let formData = {
        friendId: friendId,
        isTop: isTop
      }
      this.$http({
        url: '/friend/top',
        method: 'put',
        data: formData
      }).then(() => {
        this.friendStore.setTop(friendId, isTop)
        this.chatStore.setTop(chat, isTop)
      })
    },
    setGroupTop(chat, groupId, isTop) {
      let formData = {
        groupId: groupId,
        isTop: isTop
      }
      this.$http({
        url: '/group/top',
        method: 'put',
        data: formData
      }).then(() => {
        this.groupStore.setTop(groupId, isTop)
        this.chatStore.setTop(chat, isTop)
      })
    },
    isShowChat(chat) {
      return !this.searchText || chat.showName.includes(this.searchText)
    },
  },
  computed: {
    keyword() {
      return (this.searchKeyword || "").trim().toLowerCase();
    },
    contactResults() {
      if (!this.keyword) return [];
      const key = this.keyword;
      return this.friendStore.friends
        .filter(f => !f.deleted)
        .filter(f => {
          const showName = String(f.showNickName || "").toLowerCase();
          const nickName = String(f.nickName || "").toLowerCase();
          const remarkName = String(f.remarkNickName || "").toLowerCase();
          return showName.includes(key) || nickName.includes(key) || remarkName.includes(key);
        })
        .slice(0, 10)
        .map(f => ({ id: f.id, showName: f.showNickName, headImage: f.headImage }));
    },
    groupResults() {
      if (!this.keyword) return [];
      const key = this.keyword;
      return this.groupStore.groups
        .filter(g => !g.quit)
        .map(g => {
          const matchInfo = this.groupMatchInfo(g, key);
          return {
            id: g.id,
            showName: g.showGroupName,
            includeText: matchInfo.includeText,
            matched: matchInfo.matched
          };
        })
        .filter(g => g.matched)
        .slice(0, 10);
    },
    messageResults() {
      if (!this.keyword) return [];
      const key = this.keyword;
      const result = [];
      const chats = this.getSearchChats();
      for (const chat of chats) {
        if (chat.type === "SYSTEM" || chat.delete || !Array.isArray(chat.messages)) continue;
        let chatName = chat.showName || "会话";
        if (chat.type === "GROUP") {
          const group = this.groupStore.findGroup(chat.targetId);
          chatName = group ? group.showGroupName : (chat.showName || "群聊");
        }
        for (let i = chat.messages.length - 1; i >= 0; i--) {
          const msg = chat.messages[i];
          if (this.messageMatchesKeyword(msg, key)) {
            const text = this.getSearchableText(msg);
            result.push({
              chat,
              messageId: msg.id || null,
              groupName: chatName,
              preview: this.buildSnippet(text, this.keyword)
            });
            if (result.length >= 30) return result;
          }
        }
      }
      return result;
    },
    activeChat() {
      return this.chatStore.activeChat;
    },
    loading() {
      return this.chatStore.loading
    },
    showChats() {
      return this.chatStore.chats;
    }
  },
  mounted() {
    // 兼容旧事件，同时支持循环跳转
    this.$eventBus.$on("locateFirstUnreadChat", this.locateNextUnreadChat);
    this.$eventBus.$on("locateNextUnreadChat", this.locateNextUnreadChat);
    document.addEventListener("click", this.onGlobalClick);
  },
  beforeDestroy() {
    this.$eventBus.$off("locateFirstUnreadChat", this.locateNextUnreadChat);
    this.$eventBus.$off("locateNextUnreadChat", this.locateNextUnreadChat);
    document.removeEventListener("click", this.onGlobalClick);
  }
}
</script>

<style lang="scss">
.chat-page {
  .header {
    height: 60px;
    display: flex;
    align-items: center;
    padding: 0 12px;
    overflow: visible;

    .search-wrap {
      position: relative;
      width: 100%;
    }

    .search-popup {
      position: absolute;
      top: 38px;
      left: 0;
      // 扩展到右侧（类似 PC 微信的综合搜索）
      width: calc(100% + 120px);
      max-width: 460px;
      min-height: 320px;
      max-height: calc(100vh - 110px);
      overflow-y: auto;
      background: #fff;
      border: 1px solid rgba(0, 0, 0, 0.12);
      border-radius: 8px;
      box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18);
      z-index: 9999;

      .search-tip {
        padding: 20px;
        color: #999;
        font-size: 13px;
        text-align: center;
      }

      .search-section {
        border-bottom: 8px solid #f6f7fb;
      }
      .search-section:last-child {
        border-bottom: 0;
      }

      .section-title {
        font-size: 14px;
        color: #999;
        padding: 12px 16px 8px;
        text-align: left;
        font-weight: 600;
      }

      .section-empty {
        padding: 10px 16px 14px;
        color: #bbb;
        font-size: 12px;
      }

      .result-row {
        border-top: 1px solid #f2f2f2;
        cursor: pointer;
      }

      // 复用会话列表的排版感（左头像 + 右侧两行）
      .chat-item-like {
        height: 54px;
        display: flex;
        position: relative;
        margin: 0 3px;
        padding: 5px 8px;
        align-items: center;
        white-space: nowrap;
        border-radius: 10px;

        &:hover {
          background-color: var(--im-background-active);
        }

        .chat-left {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          flex: 0 0 auto;
        }

        .chat-right {
          flex: 1;
          display: flex;
          flex-direction: column;
          padding-left: 10px;
          text-align: left;
          overflow: hidden;
          min-width: 0;

          .chat-name {
            display: flex;
            align-items: center;
            line-height: 26px;
            height: 26px;

            .chat-name-text {
              font-size: var(--im-font-size);
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              color: var(--im-text-color);
            }
          }

          .chat-content {
            display: flex;
            line-height: 24px;
            height: 24px;

            .chat-content-text {
              flex: 1;
              font-size: var(--im-font-size-smaller);
              color: var(--im-text-color-light);
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
        }
      }

      .kw {
        color: #e34d59;
        font-weight: 600;
      }
    }
  }

  .chat-loading {
    height: 50px;
    background-color: #eee;

    .el-icon-loading {
      font-size: 24px;
      color: var(--im-text-color-light);
    }

    .el-loading-text {
      color: var(--im-text-color-light);
    }

    .chat-loading-box {
      height: 100%;
    }
  }

  .chat-items {
    flex: 1;
  }
}
</style>
